import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
  FORM_CONTROL_SELECT_CLASS,
} from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/shared/DatePicker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { useRenewContract } from '@/hooks/useContracts'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { Contract } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  contract: Contract
}

type Milestone = '30d' | '3m' | '6m' | '1y' | 'custom'

const MILESTONES: { value: Milestone }[] = [
  { value: '30d' },
  { value: '3m' },
  { value: '6m' },
  { value: '1y' },
  { value: 'custom' },
]

const dialogContentClass =
  'max-h-[calc(100vh-1.5rem)] max-w-[640px] gap-0 overflow-y-auto rounded-2xl border-[#e7e9ef] bg-white p-8 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)] sm:rounded-2xl'

const outlineBtnClass = cn(
  'h-10 rounded-xl border-[#e7e9ef] bg-white px-3 text-sm font-medium leading-none text-[#171a20] normal-case hover:bg-[#fbfbfc]',
  FORM_CONTROL_FOCUS_CLASS
)
const primaryBtnClass = cn(
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white normal-case hover:bg-[#e6511c]',
  FORM_CONTROL_FOCUS_CLASS
)
const fieldInputClass = cn(
  'h-10 rounded-xl px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20]',
  FORM_CONTROL_SELECT_CLASS
)
const dateInputClass = cn(
  'h-10 rounded-xl border border-transparent bg-[#f5f6fa] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none [color-scheme:light] [&_input]:text-sm [&_input]:font-normal [&_input]:text-[#171a20] dark:[color-scheme:dark]',
  FORM_CONTROL_FOCUS_WITHIN_CLASS
)

// Field label with an optional red required marker, matching Figma 1130:13556.
function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string
  children: React.ReactNode
  required?: boolean
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'
    >
      <span>{children}</span>
      {required && <span className='text-[#d63b33]'>*</span>}
    </Label>
  )
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex min-h-9 items-center gap-2 border-b border-[#e7e9ef] py-2 last:border-b-0'>
      <span className='w-40 shrink-0 text-xs font-medium leading-4 text-[#6c7285]'>
        {label}
      </span>
      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
        {value}
      </span>
    </div>
  )
}

function addToDate(base: Date, milestone: Milestone): Date {
  const d = new Date(base)
  if (milestone === '30d') d.setDate(d.getDate() + 30)
  if (milestone === '3m') d.setMonth(d.getMonth() + 3)
  if (milestone === '6m') d.setMonth(d.getMonth() + 6)
  if (milestone === '1y') d.setFullYear(d.getFullYear() + 1)
  d.setDate(d.getDate() - 1)
  return d
}

// 2.8.4 Extend / renew contract - milestone selector + new-period preview.
export function RenewContractDialog({ open, onOpenChange, contract }: Props) {
  const { t } = useTranslation('contracts')
  const navigate = useNavigate()
  const renew = useRenewContract(contract.id)
  const [milestone, setMilestone] = useState<Milestone>('1y')
  const [customEnd, setCustomEnd] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // New start = original end date + 1 day (2.8.4-003).
  const newStart = useMemo(() => {
    if (!contract.end_date) return null
    const d = new Date(contract.end_date)
    d.setDate(d.getDate() + 1)
    return d
  }, [contract.end_date])

  const newEnd = useMemo(() => {
    if (milestone === 'custom') return customEnd ? new Date(customEnd) : null
    if (!newStart) return null
    return addToDate(newStart, milestone)
  }, [milestone, customEnd, newStart])

  useEffect(() => {
    if (open) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setMilestone('1y')
      setCustomEnd('')
      setError(null)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [open])

  const confirm = async () => {
    if (milestone === 'custom') {
      if (!customEnd || (newStart && new Date(customEnd) <= newStart)) {
        setError(t('renew.validation.endAfterStart'))
        return
      }
    }
    if (!newEnd) {
      setError(t('renew.validation.cannotCompute'))
      return
    }
    setSaving(true)
    try {
      const res = await renew.mutateAsync({
        end_date: newEnd.toISOString().slice(0, 10),
      })
      toast({ variant: 'success', title: t('renew.success') })
      onOpenChange(false)
      if (res.data?.id) navigate(`/contracts/${res.data.id}`)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('renew.error')),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogContentClass}>
        <div className='flex flex-col gap-6'>
          <DialogTitle className='pr-8 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
            {t('renew.title')}
          </DialogTitle>

          <div className='space-y-1'>
            <FieldLabel required>{t('renew.milestoneLabel')}</FieldLabel>
            <Select
              value={milestone}
              onValueChange={v => {
                setMilestone(v as Milestone)
                setError(null)
              }}
            >
              <SelectTrigger className={fieldInputClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MILESTONES.map(m => (
                  <SelectItem key={m.value} value={m.value}>
                    {t(`renew.milestones.${m.value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {milestone === 'custom' && (
            <div className='space-y-1'>
              <FieldLabel htmlFor='custom-end'>
                {t('renew.customEndDate')}
              </FieldLabel>
              <DatePicker
                id='custom-end'
                value={customEnd}
                onChange={next => {
                  setCustomEnd(next)
                  setError(null)
                }}
                className={dateInputClass}
                error={!!error}
              />
            </div>
          )}

          <div className='flex flex-col rounded-xl border border-[#e7e9ef] bg-[#fbfbfc] px-4 py-1'>
            <PreviewRow
              label={t('renew.newStartDate')}
              value={newStart ? formatDate(newStart.toISOString()) : '-'}
            />
            <PreviewRow
              label={t('renew.newEndDate')}
              value={newEnd ? formatDate(newEnd.toISOString()) : '-'}
            />
          </div>

          {error && (
            <p className='text-xs leading-4 text-destructive'>{error}</p>
          )}

          <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
            <Button
              type='button'
              variant='outline'
              className={outlineBtnClass}
              onClick={() => onOpenChange(false)}
            >
              {t('renew.cancel')}
            </Button>
            <Button
              type='button'
              className={primaryBtnClass}
              disabled={saving}
              onClick={confirm}
            >
              {t('renew.confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RenewContractDialog
